import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import PlannerIcons from '../components/shared/PlannerIcons';

export default function PlannerProPublicView({ projectId, db, previewSettings }) {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('Overview');

    const [newNoteTitle, setNewNoteTitle] = useState('');
    const [newNoteContent, setNewNoteContent] = useState('');
    const [isSubmittingNote, setIsSubmittingNote] = useState(false);

    const [clientName, setClientName] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(`hostit_clientName_${projectId}`) || '';
        }
        return '';
    });
    const [showNamePrompt, setShowNamePrompt] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    useEffect(() => {
        if (!projectId || !db) {
            setError("Invalid Project ID");
            setLoading(false);
            return;
        }

        const projectRef = doc(db, 'plannerProjects', projectId);
        const unsubscribe = onSnapshot(projectRef, (docSnap) => {
            if (docSnap.exists()) {
                setProject({ id: docSnap.id, ...docSnap.data() });
                setError('');
            } else {
                setError("Project not found or no longer available.");
            }
            setLoading(false);
        }, (err) => {
            console.error("Error fetching shared project:", err);
            setError("Failed to load project details.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [projectId, db]);

    const requireClientName = (action) => {
        if (!clientName) {
            setPendingAction(() => action);
            setShowNamePrompt(true);
            return false;
        }
        return true;
    };

    const handleNameSubmit = (e) => {
        e.preventDefault();
        const name = new FormData(e.target).get('guestName');
        if (name && name.trim()) {
            const trimmedName = name.trim();
            localStorage.setItem(`hostit_clientName_${projectId}`, trimmedName);
            setClientName(trimmedName);
            setShowNamePrompt(false);
            if (pendingAction) pendingAction();
            setPendingAction(null);
        }
    };

    if (loading) return <div className="p-10 text-center text-gray-500">Loading Project Details...</div>;
    if (error) return <div className="p-10 text-center text-red-500 font-bold">{error}</div>;

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        const [hours, minutes] = timeStr.split(':');
        const h = parseInt(hours, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayH = h % 12 || 12;
        return `${displayH}:${minutes} ${ampm}`;
    };

    const defaultSettings = {
        showOverview: true,
        showOverviewCountdown: true,
        showOverviewLocation: true,
        showTimeline: true,
        showTimelineNotes: false,
        showTimelineAssignees: false,
        showDesign: true,
        showDesignImages: true,
        showDesignColors: true,
        showVendors: false,
        showVendorContact: false,
        showBudget: false,
        showBudgetEstimated: true,
        showBudgetActuals: false,
        showTravel: true, 
        showCatering: true, 
        showChecklist: false, 
        showGuestSummary: false, 
        showNotes: false 
    };
    
    const shareSettings = previewSettings || { ...defaultSettings, ...(project.shareSettings || {}) };

    const handleClientToggleTask = async (taskId) => {
        if (!shareSettings.allowChecklistEdits) return;
        if (!requireClientName(() => executeTaskToggle(taskId))) return;
        executeTaskToggle(taskId);
    };

    const executeTaskToggle = async (taskId) => {
        const updatedTasks = project.tasks.map(t => 
            t.id === taskId ? { ...t, completed: !t.completed } : t
        );
        try {
            const projectRef = doc(db, 'plannerProjects', project.id);
            await updateDoc(projectRef, { tasks: updatedTasks });
        } catch (error) {
            console.error("Failed to update task:", error);
            alert("Failed to update task. Please try again.");
        }
    };

    const handleClientAddNote = async (e) => {
        if (e) e.preventDefault();
        if (!shareSettings.allowNotesEdits || !newNoteTitle.trim() || !newNoteContent.trim()) return;
        if (!requireClientName(() => executeAddNote())) return;
        executeAddNote();
    };

    const executeAddNote = async () => {
        setIsSubmittingNote(true);
        try {
            const newNote = {
                id: Date.now().toString(),
                title: newNoteTitle.trim(),
                content: newNoteContent.trim(),
                date: new Date().toISOString(),
                author: clientName || 'Client'
            };
            const updatedNotes = [newNote, ...(project.notes || [])];
            const projectRef = doc(db, 'plannerProjects', project.id);
            await updateDoc(projectRef, { notes: updatedNotes });
            setNewNoteTitle('');
            setNewNoteContent('');
        } catch (error) {
            console.error("Failed to add note:", error);
            alert("Failed to add note. Please try again.");
        } finally {
            setIsSubmittingNote(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-12 font-sans">
            {showNamePrompt && (
                <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm">
                        <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl shadow-inner">👋</div>
                        <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Who's making changes?</h3>
                        <p className="text-gray-600 text-sm text-center mb-6">Please enter your name so the planner knows who updated the project.</p>
                        <form onSubmit={handleNameSubmit}>
                            <input 
                                type="text" 
                                name="guestName" 
                                required 
                                placeholder="Your Name" 
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none mb-4 font-semibold text-center" 
                                autoFocus
                            />
                            <div className="flex gap-3">
                                <button type="button" onClick={() => { setShowNamePrompt(false); setPendingAction(null); }} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 transition">Cancel</button>
                                <button type="submit" className="flex-1 py-2.5 bg-gray-900 text-white rounded-lg font-bold hover:bg-black transition shadow-md">Continue</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="h-64 md:h-80 relative">
                <img src={project.coverImage || '[https://placehold.co/1200x400/f2a2e8/ffffff](https://placehold.co/1200x400/f2a2e8/ffffff)'} className="w-full h-full object-cover" alt="Cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                <div className="absolute bottom-0 left-0 w-full p-6 md:p-8 flex flex-col md:flex-row justify-between items-end gap-4">
                    <div className="text-white">
                        <span className="text-xs font-bold uppercase tracking-widest bg-black/40 px-2 py-1 rounded backdrop-blur-sm mb-2 inline-block">Client Portal</span>
                        <h1 className="text-3xl md:text-5xl font-serif font-bold drop-shadow-md">{project.clientNames}</h1>
                        <p className="text-sm md:text-base font-medium mt-2 drop-shadow flex flex-wrap items-center gap-2">
                            <span>{project.date !== 'TBD' ? new Date(project.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'Date TBD'}</span>
                            {shareSettings.showOverviewLocation !== false && (
                                <>
                                    <span>•</span>
                                    <span>{project.location}</span>
                                    {project.venueName && (
                                        <>
                                            <span>•</span>
                                            {project.venueAddress ? (
                                                <a href={`https://maps.google.com/?q=${encodeURIComponent(project.venueAddress)}`} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1 text-white">
                                                    📍 {project.venueName}
                                                </a>
                                            ) : (
                                                <span className="flex items-center gap-1">📍 {project.venueName}</span>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8 mt-4 animate-fade-in">
                
                {shareSettings.showOverview && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-4 font-serif text-gray-900">Event Overview</h2>
                        <p className="text-gray-600 text-lg mb-6">We are so excited to celebrate with you!</p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 border-t border-gray-100 pt-6">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Date</span>
                                <p className="font-semibold text-gray-900">{project.date !== 'TBD' ? new Date(project.date).toLocaleDateString() : 'TBD'}</p>
                            </div>
                            {shareSettings.showOverviewLocation !== false && (
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Location</span>
                                    <p className="font-semibold text-gray-900">{project.location}</p>
                                    {project.venueName && (
                                        <p className="text-sm text-gray-700 mt-1">
                                            {project.venueAddress ? (
                                                <a href={`https://maps.google.com/?q=${encodeURIComponent(project.venueAddress)}`} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline font-medium">
                                                    {project.venueName}
                                                </a>
                                            ) : (
                                                <span className="font-medium">{project.venueName}</span>
                                            )}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {shareSettings.showChecklist && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Planning Checklist</h2>
                        {project.tasks && project.tasks.length > 0 ? (
                            <div className="space-y-3">
                                {project.tasks.map(task => (
                                    <div key={task.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 transition-colors">
                                        <div className="mt-0.5 flex-shrink-0">
                                            {shareSettings.allowChecklistEdits ? (
                                                <input 
                                                    type="checkbox" 
                                                    checked={task.completed || false} 
                                                    onChange={() => handleClientToggleTask(task.id)}
                                                    className="w-5 h-5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                                                />
                                            ) : (
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${task.completed ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                                                    {task.completed && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className={`font-bold text-base ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>{task.text}</h4>
                                            <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                                                {task.category && <span className="bg-gray-100 px-2 py-0.5 rounded">{task.category}</span>}
                                                {task.dueDate && <span className="bg-gray-100 px-2 py-0.5 rounded">Due: {new Date(task.dueDate).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No tasks have been added yet.</p>
                        )}
                    </div>
                )}

                {shareSettings.showGuestSummary && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Guest List Summary</h2>
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                                <p className="text-3xl font-black text-emerald-600">{project.guests?.filter(g => g.rsvp === 'Attending').length || 0}</p>
                                <p className="text-xs font-bold text-emerald-800 uppercase tracking-widest mt-1">Attending</p>
                            </div>
                            <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                                <p className="text-3xl font-black text-amber-600">{project.guests?.filter(g => g.rsvp === 'Pending').length || 0}</p>
                                <p className="text-xs font-bold text-amber-800 uppercase tracking-widest mt-1">Pending</p>
                            </div>
                            <div className="bg-rose-50 p-4 rounded-xl border border-rose-100">
                                <p className="text-3xl font-black text-rose-600">{project.guests?.filter(g => g.rsvp === 'Declined').length || 0}</p>
                                <p className="text-xs font-bold text-rose-800 uppercase tracking-widest mt-1">Declined</p>
                            </div>
                        </div>
                    </div>
                )}

                {shareSettings.showNotes && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Project Notes</h2>
                        {project.notes && project.notes.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {project.notes.map(note => (
                                    <div key={note.id} className="bg-yellow-50/50 p-5 rounded-xl border border-yellow-100 shadow-sm">
                                        <h4 className="font-bold text-gray-900 text-lg mb-2">{note.title}</h4>
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                                            {note.date ? new Date(note.date).toLocaleDateString() : ''} 
                                            {note.author && ` • By ${note.author}`}
                                        </p>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{note.content}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No project notes shared yet.</p>
                        )}
                        
                        {shareSettings.allowNotesEdits && (
                            <form onSubmit={handleClientAddNote} className="mt-8 pt-6 border-t border-gray-100 bg-gray-50 p-4 rounded-xl border">
                                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><span>✏️</span> Add a Note</h4>
                                <div className="space-y-3">
                                    <input type="text" placeholder="Note Title" value={newNoteTitle} onChange={e => setNewNoteTitle(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" required />
                                    <textarea placeholder="Write your thoughts..." value={newNoteContent} onChange={e => setNewNoteContent(e.target.value)} rows="3" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none resize-none" required></textarea>
                                    <button type="submit" disabled={isSubmittingNote} className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-black transition shadow-sm disabled:opacity-50">
                                        {isSubmittingNote ? 'Saving...' : 'Post Note'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
                
                {shareSettings.showTimeline && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Schedule & Timeline</h2>
                        {project.timeline && project.timeline.length > 0 ? (
                            <div className="space-y-4">
                                {[...project.timeline].sort((a,b) => {
                                    const dateA = a.date || '';
                                    const dateB = b.date || '';
                                    const dateCompare = dateA.localeCompare(dateB);
                                    if (dateCompare !== 0) return dateCompare;
                                    const timeA = a.time || '';
                                    const timeB = b.time || '';
                                    return timeA.localeCompare(timeB);
                                }).map((item, idx) => (
                                    <div key={idx} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-pink-200 transition-colors">
                                        <div className="flex flex-col sm:w-32 flex-shrink-0">
                                            {item.date && item.date !== project.date && (
                                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{new Date(item.date).toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric'})}</span>
                                            )}
                                            <span className="font-bold text-pink-600 text-lg">{formatTime(item.time)}</span>
                                            {item.endTime && <span className="text-xs text-gray-400 font-medium">to {formatTime(item.endTime)}</span>}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-bold text-gray-900 text-base">{item.event}</h4>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1 block">{item.category}</span>
                                            {shareSettings.showTimelineNotes && item.notes && <p className="text-sm text-gray-600 mt-2 italic border-l-2 border-gray-200 pl-3">{item.notes}</p>}
                                            {shareSettings.showTimelineAssignees && item.assigned && <p className="text-xs font-bold text-pink-600 mt-2 flex items-center gap-1"><span>👤</span> {item.assigned}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">The event schedule is being finalized.</p>
                        )}
                    </div>
                )}
                
                {shareSettings.showDesign && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Inspiration & Design</h2>
                        {project.designBoard && project.designBoard.filter(item => {
                            if (item.type === 'color' && shareSettings.showDesignColors === false) return false;
                            if (item.type === 'image' && shareSettings.showDesignImages === false) return false;
                            return true;
                        }).length > 0 ? (
                            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
                                {project.designBoard.filter(item => {
                                    if (item.type === 'color' && shareSettings.showDesignColors === false) return false;
                                    if (item.type === 'image' && shareSettings.showDesignImages === false) return false;
                                    return true;
                                }).map(item => (
                                    <div key={item.id} className="break-inside-avoid rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-gray-50 flex flex-col group">
                                        {item.type === 'image' ? (
                                            <img src={item.url} alt="Inspo" className="w-full h-auto object-cover block group-hover:scale-105 transition-transform duration-500" />
                                        ) : item.type === 'image-group' ? (
                                            <>
                                                <h4 className="font-bold text-gray-900 text-lg leading-tight p-3">{item.title}</h4>
                                                <div className="grid grid-cols-2 gap-1 px-3 pb-3">
                                                    {(item.images || []).map((url, i) => (
                                                        <img key={i} src={url} alt="Inspo" className="w-full h-24 object-cover rounded" />
                                                    ))}
                                                </div>
                                            </>
                                        ) : item.type === 'color' ? (
                                            <div className="w-full aspect-video flex flex-col items-center justify-center" style={{ backgroundColor: item.value }}>
                                                <span className="bg-white/80 px-2 py-1 rounded text-xs font-mono">{item.value}</span>
                                            </div>
                                        ) : (
                                            <div className="w-full aspect-video flex flex-col items-center justify-center" style={{ backgroundColor: item.url }}></div>
                                        )}
                                        {item.caption && (
                                            <div className="p-3 bg-white border-t border-gray-100 z-10 relative">
                                                <p className="text-sm text-gray-800 font-medium">{item.caption}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">Inspiration board coming soon.</p>
                        )}
                    </div>
                )}

                {shareSettings.showTravel && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Travel & Accommodations</h2>
                        {((project.roomBlocks && project.roomBlocks.length > 0) || (project.transportation && project.transportation.length > 0)) ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {project.roomBlocks && project.roomBlocks.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Hotel Room Blocks</h3>
                                        <div className="space-y-4">
                                            {project.roomBlocks.map(block => (
                                                <div key={block.id} className="bg-blue-50/50 p-5 rounded-xl border border-blue-100">
                                                    <h4 className="font-bold text-gray-900 text-lg">{block.hotelName}</h4>
                                                    <div className="mt-2 text-sm text-gray-700 space-y-1.5 bg-white p-3 rounded-lg border border-gray-100">
                                                        {block.rate && <p className="flex justify-between"><span className="font-bold text-gray-500 uppercase text-[10px] tracking-wide">Nightly Rate</span> <span className="font-semibold">${block.rate}</span></p>}
                                                        {block.cutoffDate && <p className="flex justify-between"><span className="font-bold text-gray-500 uppercase text-[10px] tracking-wide">Book By</span> <span className="font-semibold text-red-600">{new Date(block.cutoffDate).toLocaleDateString()}</span></p>}
                                                    </div>
                                                    {block.link && (
                                                        <a href={block.link} target="_blank" rel="noopener noreferrer" className="mt-4 block text-center bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm hover:bg-blue-700 transition">
                                                            Book a Room
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {project.transportation && project.transportation.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Shuttles & Transportation</h3>
                                        <div className="space-y-3">
                                            {project.transportation.sort((a,b) => a.time.localeCompare(b.time)).map(t => (
                                                <div key={t.id} className="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-xl items-center hover:border-gray-200 transition">
                                                    <div className="bg-white text-gray-800 font-bold px-3 py-2 rounded-lg border border-gray-200 shadow-sm font-mono text-sm shrink-0">{t.time}</div>
                                                    <div className="min-w-0">
                                                        <p className="font-bold text-gray-900 text-base truncate">{t.company} {t.vehicle && <span className="font-normal text-gray-500 text-sm ml-1">({t.vehicle})</span>}</p>
                                                        {t.route && <p className="text-sm text-gray-600 mt-0.5 truncate">{t.route}</p>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">Travel details and accommodations will be posted here.</p>
                        )}
                    </div>
                )}

                {shareSettings.showCatering && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Event Menu</h2>
                        {project.menuItems && project.menuItems.length > 0 ? (
                            <div className="space-y-8">
                                {["Hors d'oeuvres", "Starter", "Main Course", "Sides", "Dessert", "Late Night Snack", "Beverages/Bar"].map(course => {
                                    const items = project.menuItems.filter(i => i.course === course);
                                    if (items.length === 0) return null;
                                    return (
                                        <div key={course}>
                                            <h3 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2 font-serif text-pink-700">{course}</h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {items.map(item => (
                                                    <div key={item.id} className="bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-pink-100 hover:shadow-sm transition">
                                                        <h4 className="font-bold text-gray-900 text-base mb-1">{item.name}</h4>
                                                        {item.description && <p className="text-sm text-gray-600 italic mb-3 leading-relaxed">{item.description}</p>}
                                                        {item.tags && item.tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 mt-auto">
                                                                {item.tags.map((tag, idx) => (
                                                                    <span key={idx} className="bg-green-50 text-green-700 border border-green-100 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">The event menu is currently being crafted.</p>
                        )}
                    </div>
                )}

                {shareSettings.showVendors && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-2xl font-bold mb-6 font-serif text-gray-900">Vendor Team</h2>
                        {project.vendors && project.vendors.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {project.vendors.map(vendor => (
                                    <div key={vendor.id} className="p-4 border border-gray-100 bg-gray-50 rounded-xl flex justify-between items-center">
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-0.5">{vendor.category}</span>
                                            <h4 className="font-bold text-gray-900">{vendor.name}</h4>
                                        </div>
                                        {shareSettings.showVendorContact !== false && (
                                            <div className="flex gap-2">
                                                {vendor.email && <a href={`mailto:${vendor.email}`} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:text-pink-600 shadow-sm">✉️</a>}
                                                {vendor.phone && <a href={`tel:${vendor.phone}`} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:text-pink-600 shadow-sm">📞</a>}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">Vendor team is being assembled.</p>
                        )}
                    </div>
                )}

                {shareSettings.showBudget && (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold font-serif text-gray-900">Budget Overview</h2>
                            <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-bold uppercase">Confidential</span>
                        </div>
                        
                        <div className="bg-gray-900 text-white p-6 rounded-xl mb-6 flex justify-between items-center">
                            {shareSettings.showBudgetEstimated !== false && (
                                <div>
                                    <span className="text-gray-400 text-sm font-bold uppercase tracking-wider block mb-1">Total Estimated</span>
                                    <span className="text-3xl font-bold">${(project.budgetItems || []).reduce((a,b) => a + (Number(b.estimated) || 0), 0).toLocaleString()}</span>
                                </div>
                            )}
                            {shareSettings.showBudgetActuals && (
                                <div>
                                    <span className="text-gray-400 text-sm font-bold uppercase tracking-wider block mb-1">Total Actuals</span>
                                    <span className="text-3xl font-bold">${(project.budgetItems || []).reduce((a,b) => a + (Number(b.actual) || 0), 0).toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                        
                        {shareSettings.showBudgetActuals && (
                            <div className="mt-4">
                                <h3 className="font-bold text-gray-800 mb-3">Actual Expenses Breakdown</h3>
                                {project.budgetItems && project.budgetItems.length > 0 ? (
                                    <div className="space-y-2">
                                        {project.budgetItems.map(item => (
                                            <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                                                <div>
                                                    <p className="font-bold text-gray-900">{item.name}</p>
                                                    <p className="text-xs text-gray-500">{item.category}</p>
                                                </div>
                                                <span className="font-bold text-gray-900">${(Number(item.actual) || 0).toLocaleString()}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">No actual expenses recorded yet.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <div className="text-center pb-8 opacity-50">
                <p className="text-sm font-bold">Powered by Host-it! Pro</p>
            </div>
        </div>
    );
}
